import React from "react";
import Reveal from "./Reveal";

// Editorial gallery — real HIT inaugural edition photos.
// Structured to read like a magazine spread: stage → mentors → pods → kit → winners.
const PHOTOS = [
  {
    id: "02",
    src: "/assets/hit/audience-qa.jpg",
    label: "AUDIENCE Q&A · GRAND FINALE",
    aspect: "3/2",
    span: "col-span-12 md:col-span-7",
    pos: "center 30%",
  },
  {
    id: "03",
    src: "/assets/hit/founder-address.jpg",
    label: "ON STAGE · FOUNDER ADDRESS",
    aspect: "3/2",
    span: "col-span-12 md:col-span-5",
    pos: "center 30%",
  },
  {
    id: "04",
    src: "/assets/hit/mentor-gift-1.jpg",
    label: "MENTOR RECOGNITION · POD 10 · MERIDIAN",
    aspect: "1/1",
    span: "col-span-6 md:col-span-4",
    pos: "center 30%",
  },
  {
    id: "05",
    src: "/assets/hit/mentor-gift-2.jpg",
    label: "MENTOR HANDOVER · FACULTY HONOURS",
    aspect: "1/1",
    span: "col-span-6 md:col-span-4",
    pos: "center 30%",
  },
  {
    id: "06",
    src: "/assets/hit/mentor-gift-3.jpg",
    label: "MENTOR HANDOVER · STAGE RECEIPT",
    aspect: "1/1",
    span: "col-span-6 md:col-span-4",
    pos: "center 30%",
  },
  {
    id: "07",
    src: "/assets/hit/gift-hit.jpg",
    label: "HIT EDITION · COMMEMORATIVE BOX",
    aspect: "3/2",
    span: "col-span-12 md:col-span-5",
    pos: "center 40%",
  },
  {
    id: "08",
    src: "/assets/hit/prepgenius-team.jpg",
    label: "PREPGENIUS · POD 44 · ₹5K PRIZE COHORT",
    aspect: "3/2",
    span: "col-span-12 md:col-span-7",
    pos: "center 30%",
  },
  {
    id: "09",
    src: "/assets/hit/pod-kit.jpg",
    label: "POD KIT · BRAND DETAIL",
    aspect: "1/1",
    span: "col-span-6 md:col-span-4",
    pos: "center",
  },
  {
    id: "10",
    src: "/assets/hit/winners.jpg",
    label: "WINNERS' MOMENT · ₹50K · ₹30K · ₹10K",
    aspect: "16/9",
    span: "col-span-12 md:col-span-8",
    pos: "center 35%",
  },
];

function Photo({ id, src, label, aspect, pos = "center" }) {
  return (
    <figure
      className="relative card-edge overflow-hidden"
      style={{ borderRadius: 4 }}
      data-testid={`hit-photo-${id}`}
    >
      <div className="w-full overflow-hidden" style={{ aspectRatio: aspect }}>
        <img
          src={src}
          alt={label}
          className="w-full h-full object-cover"
          style={{ objectPosition: pos }}
          loading="lazy"
        />
      </div>
      <figcaption
        className="p-3 font-mono text-[10.5px] flex items-center gap-2 border-t hairline"
        style={{ color: "var(--ink-tertiary)", letterSpacing: "0.12em" }}
      >
        <span style={{ color: "var(--accent-gold)" }}>{id}</span>
        <span aria-hidden="true">·</span>
        <span>{label}</span>
      </figcaption>
    </figure>
  );
}

export default function InauguralEdition() {
  return (
    <section
      id="inaugural"
      className="py-10 md:py-14 border-t hairline"
      style={{ background: "var(--canvas-deep)" }}
      data-testid="inaugural-section"
    >
      <div className="container-edge">
        <Reveal>
          <p className="section-number">11 — INSIDE THE INNOVATION ARENA</p>
        </Reveal>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <Reveal>
              <h2
                className="font-display text-[36px] sm:text-[44px] lg:text-[56px] leading-[1.0] tracking-tight"
                style={{ fontWeight: 500 }}
              >
                May 2026 · Real cohorts. Real winners. Real cheques.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <Reveal delay={120}>
              <p
                className="text-[15px] md:text-[16px] leading-relaxed"
                style={{ color: "var(--ink-secondary)" }}
              >
                700+ trained. 70 pods. 10 finalists pitched. NASSCOM and industry leaders
                judged. ₹90K in cheques handed out on stage. 15 builders are now being backed.
                Spring Editions open March 1.
              </p>
            </Reveal>
          </div>
        </div>

        {/* Featured photo — the grand finale wide */}
        <Reveal delay={140}>
          <figure
            className="mt-8 relative card-edge overflow-hidden"
            style={{ borderRadius: 4 }}
            data-testid="hit-featured"
          >
            <div className="w-full overflow-hidden" style={{ aspectRatio: "16/9" }}>
              <img
                src="/assets/hit/grand-finale.jpg"
                alt="The Grand Finale — AI Innovation Arena 2026, HIT Edition"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 40%" }}
                loading="lazy"
              />
            </div>
            <figcaption
              className="p-4 font-mono text-[11px] flex items-center gap-3 border-t hairline"
              style={{ color: "var(--ink-tertiary)", letterSpacing: "0.14em" }}
            >
              <span style={{ color: "var(--accent-gold)" }}>01</span>
              <span aria-hidden="true">·</span>
              <span>THE GRAND FINALE · 16 MAY 2026 · INDIA</span>
            </figcaption>
          </figure>
        </Reveal>

        {/* Editorial grid — 2 wide rows */}
        <div className="mt-6 grid grid-cols-12 gap-3 md:gap-4">
          {PHOTOS.map((p, i) => (
            <Reveal key={p.id} delay={i * 60} className={p.span}>
              <Photo {...p} />
            </Reveal>
          ))}
        </div>

        {/* CTA strip */}
        <Reveal delay={200}>
          <div
            className="mt-10 p-6 lg:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-5 card-edge"
            data-testid="hit-cta-strip"
          >
            <div>
              <p
                className="font-display text-[22px] md:text-[26px] leading-tight"
                style={{ fontWeight: 500 }}
              >
                Want this on your campus?
              </p>
              <p
                className="mt-2 text-[14px]"
                style={{ color: "var(--ink-secondary)" }}
              >
                The AI Innovation Arena 2026 Spring Editions open March 1.
              </p>
            </div>
            <a
              href="#enquiry"
              className="btn-primary whitespace-nowrap"
              data-testid="hit-host-cta"
              onClick={() => {
                try {
                  window.dispatchEvent(
                    new CustomEvent("enquiry:prefill", { detail: { audience: "college" } })
                  );
                } catch (_) {}
              }}
            >
              Apply to host an edition →
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
